import maplibregl from 'maplibre-gl';
import type { Map as MlMap, MapMouseEvent } from 'maplibre-gl';
import { totemLayerIds } from './totems-layer';
import { selectionHtml, type Selection } from './details-panel';
import type { KingdomProperties, StructureProperties } from '../api/models';

// Hover tooltip: a read-only glance at the same details the click panel
// shows. Hover targets are structures (totem or host footprint) and kingdom
// NAME LABELS — deliberately not the kingdom fill, which would tooltip the
// whole map. Click still opens the details panel, where management controls
// live. The tooltip is pointer-events: none (styles.css) so it never eats
// the click underneath it.

// hoverSelection maps a hovered feature to a Selection by which layer it
// came from: any structure layer → structure; otherwise it's the kingdom
// label layer. Pure, so it unit-tests directly.
export function hoverSelection(
  layerId: string,
  properties: unknown,
  structureLayers: readonly string[],
): Selection {
  return structureLayers.includes(layerId)
    ? { kind: 'structure', props: properties as StructureProperties }
    : { kind: 'kingdom', props: properties as KingdomProperties };
}

// hoverKey is a stable identity for the hovered target so mousemove rebuilds
// the tooltip only when the target actually changes, not per pixel.
export function hoverKey(s: Selection): string {
  if (s.kind === 'kingdom') return `k:${s.props.name}`;
  // Plots never reach hover (it only queries structure/kingdom layers).
  if (s.kind === 'plot') return `p:${s.props.building_id}`;
  const p = s.props;
  return `s:${p.name}:${p.builder_username}:${p.label_lng},${p.label_lat}`;
}

export function wireHover(map: MlMap): void {
  const popup = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    maxWidth: '240px',
    offset: 14,
    className: 'hover-popup',
  });
  let currentKey: string | null = null;

  const clear = (): void => {
    currentKey = null;
    popup.remove();
    map.getCanvas().style.cursor = '';
  };

  map.on('mousemove', (e: MapMouseEvent) => {
    const structureLayers = [...totemLayerIds(), 'structures-fill'].filter(
      (id) => map.getLayer(id) !== undefined,
    );
    const layers = [...structureLayers];
    if (map.getLayer('kingdoms-label') !== undefined) {
      layers.push('kingdoms-label');
    }
    const feature =
      layers.length > 0
        ? map.queryRenderedFeatures(e.point, { layers })[0]
        : undefined;
    if (!feature) {
      clear();
      return;
    }
    const selection = hoverSelection(
      feature.layer.id,
      feature.properties,
      structureLayers,
    );
    const key = hoverKey(selection);
    popup.setLngLat(e.lngLat);
    if (key !== currentKey) {
      currentKey = key;
      popup.setHTML(selectionHtml(selection)).addTo(map);
      map.getCanvas().style.cursor = 'pointer';
    }
  });
  map.on('mouseout', clear);
}
